// Vehicle lookup for the Rim finder: VIN, plate-to-VIN proxy, or year/make/model. Resolves the
// bolt pattern and factory wheel size, remembers the vehicle on this device, and
// asks the surrounding <rim-finder> to show wheels that fit.
(() => {
  if (customElements.get('vehicle-lookup')) return;

  const STORAGE_KEY = 'marcos-tires:vehicle';
  const NHTSA_URL = 'https://vpic.nhtsa.dot.gov/api/vehicles/DecodeVinValuesExtended/';
  const VIN_PATTERN = /^[A-HJ-NPR-Z0-9]{17}$/;
  const PLATE_PATTERN = /^[A-Z0-9]{1,8}$/;
  const STATES = new Set('AL AK AZ AR CA CO CT DE DC FL GA HI ID IL IN IA KS KY LA ME MD MA MI MN MS MO MT NE NV NH NJ NM NY NC ND OH OK OR PA RI SC SD TN TX UT VT VA WA WV WI WY'.split(' '));
  const VIN_VALUES = { A: 1, B: 2, C: 3, D: 4, E: 5, F: 6, G: 7, H: 8, J: 1, K: 2, L: 3, M: 4, N: 5, P: 7, R: 9, S: 2, T: 3, U: 4, V: 5, W: 6, X: 7, Y: 8, Z: 9 };
  const VIN_WEIGHTS = [8, 7, 6, 5, 4, 3, 2, 10, 0, 9, 8, 7, 6, 5, 4, 3, 2];
  const UPPERCASE_MAKES = new Set(['BMW', 'GMC', 'MINI', 'SRT']);
  const BODY_NAMES = { 'sport utility vehicle': 'SUV', 'sedan': 'Sedan', 'hatchback': 'Hatchback', 'convertible': 'Convertible', 'crossover utility vehicle': 'Crossover', 'pickup': 'Pickup' };

  const normalize = (value) => String(value ?? '').toLowerCase().normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9.]+/g, ' ').trim();
  const compact = (value) => normalize(value).replace(/\s/g, '');
  const element = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  };
  const readJson = (root, selector, fallback) => {
    try { return JSON.parse(root.querySelector(selector)?.textContent || '') ?? fallback; } catch (_) { return fallback; }
  };

  const vinCheckDigitValid = (vin) => {
    const sum = [...vin].reduce((total, char, index) => total + (/\d/.test(char) ? Number(char) : VIN_VALUES[char]) * VIN_WEIGHTS[index], 0);
    return vin[8] === (sum % 11 === 10 ? 'X' : String(sum % 11));
  };

  // Uppercase, drop separators and a pasted "VIN" prefix, and replace I/O/Q (never valid in a VIN).
  const cleanVin = (value) => {
    let vin = String(value || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
    if (vin.length > 17 && vin.startsWith('VIN')) vin = vin.slice(3);
    const corrected = vin.replace(/[IOQ]/g, (char) => (char === 'I' ? '1' : '0'));
    return { vin: corrected.slice(0, 17), corrected: corrected !== vin };
  };

  const titleCase = (value) => String(value || '').split(/(\s+|-)/).map((part) => {
    if (UPPERCASE_MAKES.has(part.toUpperCase())) return part.toUpperCase();
    return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
  }).join('');
  // "Sport Utility Vehicle [SUV]/Multipurpose Vehicle [MPV]" -> "SUV", "Sedan/Saloon" -> "Sedan".
  const shortBody = (value) => {
    const first = String(value || '').split('/')[0].replace(/\s*[[(].*$/, '').trim();
    return BODY_NAMES[normalize(first)] || first;
  };
  const shortDrive = (value) => {
    const first = String(value || '').split('/')[0].trim();
    return first === '4x2' ? '2WD' : first;
  };
  const shortTransmission = (value) => {
    const abbreviation = String(value || '').match(/\(([A-Z]{2,4})\)/);
    return abbreviation ? abbreviation[1] : String(value || '').split('/')[0].trim();
  };
  const transmissionKind = (value) => {
    const text = normalize(value);
    if (/manual|standard/.test(text)) return 'manual';
    if (/automatic|continuously|cvt|dual clutch|dct|automated/.test(text)) return 'automatic';
    return '';
  };
  // "5x114.3" -> "5×114.3 (5×4.5″)"; the inch form is shown only when it is an exact eighth.
  const formatBolt = (bolt) => {
    const [lugs, pcd] = String(bolt).split('x');
    const inches = Number(pcd) / 25.4;
    const eighths = Math.round(inches * 8);
    const inchText = Math.abs(inches * 8 - eighths) < 0.02 ? ` (${lugs}×${eighths / 8}″)` : '';
    return { main: `${lugs}×${pcd}`, inches: inchText };
  };

  class VehicleLookup extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.strings = readJson(this, '[data-vehicle-strings]', {});
      this.oemReference = readJson(this, '[data-vehicle-reference]', {});
      this.shopPhotos = readJson(this, '[data-vehicle-images]', []);
      this.modeButtons = [...this.querySelectorAll('[data-lookup-mode]')];
      this.panels = Object.fromEntries([...this.querySelectorAll('[data-lookup-panel]')].map((panel) => [panel.dataset.lookupPanel, panel]));
      this.vinInput = this.querySelector('[data-vin-input]');
      this.vinCount = this.querySelector('[data-vin-count]');
      this.vinNote = this.querySelector('[data-vin-note]');
      this.vinNoteText = this.vinNote?.textContent || '';
      this.plateInput = this.querySelector('[data-plate-input]');
      this.plateState = this.querySelector('[data-plate-state]');
      this.ymm = { year: this.querySelector('[data-ymm-year]'), make: this.querySelector('[data-ymm-make]'), model: this.querySelector('[data-ymm-model]') };
      this.message = this.querySelector('[data-vehicle-message]');
      this.result = this.querySelector('[data-vehicle-result]');
      this.mode = 'vin';
      this.requestId = 0;
      // Phones start with the lookup folded to its heading so the catalog is reached sooner; a saved
      // vehicle (restored below) unfolds it again. The toggle is hidden on wider screens.
      this.toggle = this.querySelector('[data-vehicle-toggle]');
      this.toggle?.addEventListener('click', () => this.setCollapsed(!this.hasAttribute('data-collapsed')));
      if (this.toggle && !this.hasAttribute('data-start-expanded') && matchMedia('(max-width: 749px)').matches) this.setCollapsed(true);

      this.modeButtons.forEach((button) => button.addEventListener('click', () => this.setMode(button.dataset.lookupMode)));
      this.panels.vin?.addEventListener('submit', (event) => { event.preventDefault(); this.submitVin(); });
      this.panels.ymm?.addEventListener('submit', (event) => { event.preventDefault(); this.submitYmm(); });
      this.panels.plate?.addEventListener('submit', (event) => { event.preventDefault(); this.submitPlate(); });
      this.plateInput?.addEventListener('input', () => { this.plateInput.removeAttribute('aria-invalid'); this.cancel(); });
      this.plateState?.addEventListener('change', () => { this.plateState.removeAttribute('aria-invalid'); this.cancel(); });
      this.vinInput?.addEventListener('input', () => this.handleVinInput());
      this.ymm.year?.addEventListener('change', () => this.fillMakes());
      this.ymm.make?.addEventListener('change', () => this.fillModels());
      this.addEventListener('click', (event) => {
        const action = event.target.closest('[data-vehicle-action]')?.dataset.vehicleAction;
        if (action === 'shop') this.shop();
        else if (action === 'shop-size') this.shop({ sizeOnly: true });
        else if (action === 'change') this.changeVehicle();
        else if (action === 'use-vin') { this.changeVehicle(); this.setMode('vin'); }
        else if (action === 'force-vin') this.submitVin({ force: true });
      });
      if (this.dataset.defaultMode && this.panels[this.dataset.defaultMode]) this.setMode(this.dataset.defaultMode, { focus: false });
      this.restoreVehicle();
    }

    disconnectedCallback() { this.controller?.abort(); }

    get t() { return this.strings; }

    setCollapsed(collapsed) {
      this.toggleAttribute('data-collapsed', collapsed);
      this.toggle?.setAttribute('aria-expanded', String(!collapsed));
    }

    setMode(mode, { focus = true } = {}) {
      if (!this.panels[mode]) return;
      this.mode = mode;
      this.cancel();
      this.modeButtons.forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.lookupMode === mode)));
      Object.entries(this.panels).forEach(([key, panel]) => { panel.hidden = key !== mode; });
      if (mode === 'ymm') this.fillYears();
      if (focus) this.panels[mode].querySelector('input, select:not(:disabled)')?.focus();
    }

    // Aborts any lookup in flight and clears its message; the saved vehicle is untouched.
    cancel() {
      this.controller?.abort();
      this.requestId += 1;
      this.setBusy(false);
      this.showMessage('');
    }

    setBusy(busy) {
      this.querySelectorAll('[data-lookup-panel] [type="submit"]').forEach((button) => {
        button.dataset.label ||= button.textContent;
        button.disabled = busy;
        button.textContent = busy ? this.t.checking : button.dataset.label;
      });
      this.toggleAttribute('aria-busy', busy);
    }

    showMessage(text, tone = 'error', action) {
      if (!this.message) return;
      this.message.replaceChildren();
      this.message.dataset.tone = tone;
      if (!text) return;
      this.message.append(element('p', '', text));
      if (action) {
        const button = element('button', 'vehicle-lookup__text-button', action.label);
        button.type = 'button';
        button.dataset.vehicleAction = action.name;
        this.message.append(button);
      }
    }

    handleVinInput() {
      const { vin, corrected } = cleanVin(this.vinInput.value);
      if (this.vinInput.value !== vin) this.vinInput.value = vin;
      this.vinInput.removeAttribute('aria-invalid');
      if (this.vinCount) this.vinCount.textContent = `${vin.length}/17`;
      if (this.vinNote) this.vinNote.textContent = corrected ? this.t.vinCorrected : this.vinNoteText;
      this.cancel();
    }

    invalid(input, text, action) {
      input?.setAttribute('aria-invalid', 'true');
      this.showMessage(text, 'error', action);
      input?.focus();
    }

    async submitVin({ force = false } = {}) {
      const { vin } = cleanVin(this.vinInput?.value);
      if (vin.length !== 17) return this.invalid(this.vinInput, this.t.vinLength.replace('[count]', vin.length));
      if (!VIN_PATTERN.test(vin)) return this.invalid(this.vinInput, this.t.vinInvalid);
      if (!force && !vinCheckDigitValid(vin)) {
        return this.invalid(this.vinInput, this.t.vinCheckDigit, { name: 'force-vin', label: this.t.lookupAnyway });
      }
      await this.run(this.t.vinLoading, this.t.vinError, async (signal) => {
        const vehicle = await this.decodeVin(vin, { signal, force, source: 'vin' });
        if (!vehicle) { this.invalid(this.vinInput, this.t.vinNotFound); return null; }
        return vehicle;
      });
    }

    async submitYmm() {
      const list = await this.loadBoltReference();
      if (!list) return this.showMessage(this.t.ymmError);
      const year = Number(this.ymm.year?.value);
      const entry = this.ymm.model?.value ? list[Number(this.ymm.model.value)] : null;
      if (!entry || !year) {
        const missing = [this.ymm.year, this.ymm.make, this.ymm.model].find((select) => select && !select.value);
        return this.invalid(missing || this.ymm.year, this.t.ymmIncomplete);
      }
      await this.showVehicle({ source: 'ymm', year, make: entry.make, model: entry.model, referenceId: entry.id }, { focus: true });
    }

    async submitPlate() {
      let endpoint;
      try { endpoint = new URL(this.dataset.plateEndpoint); } catch (_) { return this.showMessage(this.t.plateError); }
      if (endpoint.protocol !== 'https:' || endpoint.username || endpoint.password) return this.showMessage(this.t.plateError);
      const plate = String(this.plateInput?.value || '').toUpperCase().replace(/[\s-]/g, '');
      const state = String(this.plateState?.value || '').toUpperCase();
      if (!PLATE_PATTERN.test(plate)) return this.invalid(this.plateInput, this.t.plateInvalid);
      if (!STATES.has(state)) return this.invalid(this.plateState, this.t.plateInvalid);
      this.plateInput.value = plate;
      await this.run(this.t.plateLoading, this.t.plateError, async (signal) => {
        const response = await fetch(endpoint.href, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ plate, state }),
          signal, credentials: 'omit', cache: 'no-store', referrerPolicy: 'no-referrer'
        });
        if (signal.aborted) return null;
        if (response.status === 404) { this.showMessage(this.t.plateNotFound); return null; }
        if (response.status === 409) { this.showMessage(this.t.plateAmbiguous); return null; }
        if (response.status === 429) { this.showMessage(this.t.plateLimited); return null; }
        if (!response.ok) throw new Error('Plate service unavailable');
        const data = await response.json();
        if (!VIN_PATTERN.test(String(data?.vin || ''))) throw new Error('Invalid plate response');
        const vehicle = await this.decodeVin(data.vin, { signal, force: false, source: 'plate' });
        if (!vehicle) { this.showMessage(this.t.plateNotFound); return null; }
        // Remember the decoded vehicle, never the submitted plate or registration state.
        this.plateInput.value = '';
        return vehicle;
      });
    }

    // Shared lifecycle for network lookups: one request at a time, 15 s timeout, stale replies ignored.
    async run(loadingText, errorText, task) {
      this.controller?.abort();
      const id = this.requestId += 1;
      const controller = this.controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 15000);
      this.setBusy(true);
      this.showMessage(loadingText, 'loading');
      try {
        const vehicle = await task(controller.signal);
        if (id !== this.requestId || !this.isConnected) return;
        if (vehicle) { this.showMessage(''); await this.showVehicle(vehicle, { focus: true }); }
      } catch (_) {
        if (id === this.requestId && this.isConnected) this.showMessage(errorText);
      } finally {
        clearTimeout(timer);
        if (id === this.requestId) this.setBusy(false);
      }
    }

    async decodeVin(vin, { signal, force, source }) {
      const response = await fetch(`${NHTSA_URL}${encodeURIComponent(vin)}?format=json`, { signal, referrerPolicy: 'no-referrer' });
      if (!response.ok) throw new Error('VIN service unavailable');
      const raw = (await response.json())?.Results?.[0];
      // 0 = clean decode; 1 = check digit mismatch, accepted only when the customer chose "look up anyway".
      const codes = String(raw?.ErrorCode ?? '0').split(',').map((code) => code.trim()).filter(Boolean);
      const accepted = force ? ['0', '1'] : ['0'];
      if (!raw?.Make || !raw?.Model || !raw?.ModelYear || !codes.every((code) => accepted.includes(code))) return null;
      return {
        source, vin,
        year: Number(raw.ModelYear), make: raw.Make, model: raw.Model, series: raw.Series || '', trim: raw.Trim || '',
        body: raw.BodyClass || raw.VehicleType || '', drive: raw.DriveType || '', fuel: raw.FuelTypePrimary || '',
        cylinders: raw.EngineCylinders || '', displacement: Number(raw.DisplacementL) || 0,
        transmission: raw.TransmissionStyle || '', tpms: raw.TPMS || '',
        wheelFront: Number(raw.WheelSizeFront) || null, wheelRear: Number(raw.WheelSizeRear) || null
      };
    }

    loadBoltReference() {
      this.boltReference ||= fetch(this.dataset.referenceUrl)
        .then((response) => { if (!response.ok) throw new Error('Reference unavailable'); return response.json(); })
        .then((data) => (Array.isArray(data.vehicles) ? data.vehicles : []))
        .catch(() => { this.boltReference = null; return null; });
      return this.boltReference;
    }

    referenceMatches(item, vehicle, { identityOnly = false } = {}) {
      const names = [item.model, ...(item.aliases || [])].map(compact);
      const identity = compact(item.make) === compact(vehicle.make) && names.includes(compact(vehicle.model))
        && vehicle.year >= item.years[0] && vehicle.year <= item.years[1];
      if (identityOnly) return identity;
      const trimText = ` ${normalize(vehicle.trim)} `;
      return identity
        && (!item.body || normalize(item.body) === normalize(vehicle.body))
        && (!item.trim || normalize(item.trim) === normalize(vehicle.trim))
        && (!item.cylinders || Number(item.cylinders) === Number(vehicle.cylinders))
        && (!item.match_trims || item.match_trims.some((trim) => trimText.includes(` ${normalize(trim)} `)))
        && (!item.series || item.series.map(compact).includes(compact(vehicle.series)))
        && !(item.exclude_trims || []).some((trim) => trimText.includes(` ${normalize(trim)} `));
    }

    async resolveFitment(vehicle) {
      let bolt = null;
      let displayMake = titleCase(vehicle.make);
      let displayModel = vehicle.model;
      const reference = await this.loadBoltReference() || [];
      const selected = vehicle.referenceId ? reference.find((item) => item.id === vehicle.referenceId && this.referenceMatches(item, vehicle, { identityOnly: true })) : null;
      const matches = reference.filter((item) => (item.vin !== false || item.match_trims) && this.referenceMatches(item, vehicle));
      const patterns = new Set(matches.map((item) => item.bolt));
      const entry = selected || (patterns.size === 1 ? matches[0] : null);
      const boltOptions = reference.filter((item) => item.vin === false && this.referenceMatches(item, vehicle, { identityOnly: true }));
      if (entry) { bolt = entry.bolt; displayMake = entry.make; displayModel = entry.label || vehicle.model; }
      const oemEntry = (this.oemReference.wheels || []).find((item) => this.referenceMatches(item, vehicle));
      let oem = null;
      if (oemEntry) {
        const kind = transmissionKind(vehicle.transmission);
        const exact = kind ? oemEntry.options.filter((option) => option.transmission === kind) : [];
        oem = { source: oemEntry.source, options: exact.length ? exact : oemEntry.options, ambiguous: !exact.length && oemEntry.options.length > 1 };
      }
      const valid = (size) => Number.isFinite(size) && size >= 13 && size <= 30;
      const front = valid(vehicle.wheelFront) ? vehicle.wheelFront : null;
      const rear = valid(vehicle.wheelRear) ? vehicle.wheelRear : null;
      const factory = oem && oem.options.length === 1 ? Number.parseFloat(oem.options[0].wheel) : front || rear;
      return { bolt, boltOptions, boltSource: entry?.source, boltProvider: entry?.provider, oem, front, rear, factory: Number.isFinite(factory) ? factory : null, displayMake, displayModel };
    }

    async showVehicle(vehicle, { focus = false, save = true } = {}) {
      const renderVersion = this.renderVersion = (this.renderVersion || 0) + 1;
      const requestId = this.requestId;
      const fitment = await this.resolveFitment(vehicle);
      if (renderVersion !== this.renderVersion || requestId !== this.requestId || !this.isConnected) return;
      this.vehicle = vehicle;
      this.fitment = fitment;
      this.label = `${vehicle.year} ${fitment.displayMake} ${fitment.displayModel}`;
      this.result.replaceChildren(this.buildCard(vehicle, fitment));
      this.result.hidden = false;
      this.dispatchEvent(new CustomEvent('vehicle-lookup:resolved', { bubbles: true, detail: { year: vehicle.year, make: vehicle.make, model: vehicle.model } }));
      this.dataset.state = 'vehicle';
      this.setCollapsed(false);
      if (save) { try { localStorage.setItem(STORAGE_KEY, JSON.stringify({ version: 1, vehicle })); } catch (_) {} }
      if (focus) this.result.querySelector('.vehicle-card__title')?.focus();
    }

    restoreVehicle() {
      let saved;
      try { saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null'); } catch (_) { return; }
      const vehicle = saved?.version === 1 ? saved.vehicle : null;
      if (!vehicle || !vehicle.year || !vehicle.make || !vehicle.model) return;
      this.showVehicle(vehicle, { save: false }).catch(() => {});
    }

    changeVehicle() {
      this.renderVersion = (this.renderVersion || 0) + 1;
      try { localStorage.removeItem(STORAGE_KEY); } catch (_) {}
      this.vehicle = null;
      this.result.replaceChildren();
      this.result.hidden = true;
      this.dataset.state = 'search';
      this.cancel();
      this.dispatchEvent(new CustomEvent('vehicle-lookup:clear', { bubbles: true }));
      this.panels[this.mode]?.querySelector('input, select:not(:disabled)')?.focus();
    }

    shop({ sizeOnly = false } = {}) {
      if (!this.vehicle) return;
      const bolt = sizeOnly ? null : this.fitment.bolt;
      this.dispatchEvent(new CustomEvent('vehicle-lookup:shop', {
        bubbles: true, detail: { label: this.label, bolt, boltLabel: bolt ? formatBolt(bolt).main : '', factory: this.fitment.factory }
      }));
    }

    photoFor(vehicle) {
      const shopPhoto = this.shopPhotos.find((item) => item.image && String(item.year) === String(vehicle.year)
        && normalize(item.make) === normalize(vehicle.make) && normalize(item.model) === normalize(vehicle.model));
      return shopPhoto || (this.oemReference.photos || []).find((item) => this.referenceMatches(item, vehicle));
    }

    buildCard(vehicle, fitment) {
      const t = this.t;
      const card = element('div', 'vehicle-card');
      const header = element('div', 'vehicle-card__header');
      const visual = element('div', 'vehicle-card__visual');
      const icon = this.querySelector('[data-vehicle-icon] svg')?.cloneNode(true);
      if (icon) visual.append(icon);
      const photo = this.photoFor(vehicle);
      if (photo) {
        const image = element('img');
        image.alt = ''; image.width = 160; image.height = 132; image.decoding = 'async';
        image.addEventListener('load', () => visual.replaceChildren(image), { once: true });
        image.src = photo.image;
      }
      const identity = element('div', 'vehicle-card__identity');
      const title = element('h4', 'vehicle-card__title', this.label);
      title.tabIndex = -1;
      identity.append(title);
      const meta = [vehicle.trim, shortBody(vehicle.body)].filter(Boolean).join(' · ');
      if (meta) identity.append(element('p', 'vehicle-card__meta', meta));
      if (vehicle.vin) identity.append(element('p', 'vehicle-card__vin', `VIN ${vehicle.vin}`));
      const change = element('button', 'vehicle-lookup__text-button vehicle-card__change', t.change);
      change.type = 'button'; change.dataset.vehicleAction = 'change';
      change.setAttribute('aria-label', `${t.change}: ${this.label}`);
      header.append(visual, identity, change);
      card.append(header);

      const specs = element('dl', 'vehicle-card__specs');
      const addSpec = (label, value, extra) => {
        const row = element('div');
        const detail = element('dd', '', value);
        if (extra) detail.append(element('span', '', extra));
        row.append(element('dt', '', label), detail);
        specs.append(row);
      };
      if (fitment.bolt) { const bolt = formatBolt(fitment.bolt); addSpec(t.boltPattern, bolt.main, bolt.inches); }
      if (fitment.oem) {
        fitment.oem.options.forEach((option) => {
          const label = fitment.oem.options.length > 1 ? `${t.factoryWheel} · ${option.transmission === 'manual' ? t.manual : t.automatic}` : t.factoryWheel;
          addSpec(label, option.wheel);
          addSpec(t.factoryTires, option.tire);
        });
      } else if (fitment.front && fitment.rear && fitment.front !== fitment.rear) {
        addSpec(t.front, `${fitment.front} in`);
        addSpec(t.rear, `${fitment.rear} in`);
      } else if (fitment.factory) {
        addSpec(t.factoryWheel, `${fitment.factory} in`);
      }
      if (fitment.oem?.ambiguous) card.append(element('p', 'vehicle-card__note', t.oemOptions));
      if (specs.children.length) card.append(specs);

      const actions = element('div', 'vehicle-card__actions');
      if (fitment.bolt) {
        const shop = element('button', 'vehicle-card__shop', t.shopFit);
        shop.type = 'button'; shop.dataset.vehicleAction = 'shop';
        actions.append(shop);
      } else {
        if (fitment.boltOptions.length) {
          const label = element('label', 'vehicle-card__variant', t.vehicleVariant);
          const select = element('select', 'vehicle-card__variant-select');
          select.dataset.vehicleVariant = '';
          select.append(new Option(t.selectVehicleVariant, ''));
          fitment.boltOptions.forEach((item) => select.add(new Option(item.label || item.model, item.id)));
          select.addEventListener('change', async () => {
            if (!select.value) return;
            await this.showVehicle({ ...vehicle, referenceId: select.value }, { focus: true });
            this.dispatchEvent(new CustomEvent('vehicle-lookup:clear', { bubbles: true }));
          });
          label.append(select);
          actions.append(element('p', 'vehicle-card__notice', t.variantNote), label);
        } else actions.append(element('p', 'vehicle-card__notice', t.boltUnknown));
        if (fitment.factory) {
          const browse = element('button', 'vehicle-card__secondary', t.shopSize.replace('[size]', fitment.factory));
          browse.type = 'button'; browse.dataset.vehicleAction = 'shop-size';
          actions.append(browse);
        }
      }
      card.append(actions);

      if (vehicle.source === 'ymm') {
        const note = element('p', 'vehicle-card__note', t.ymmTrimNote);
        const useVin = element('button', 'vehicle-lookup__text-button', t.useVin);
        useVin.type = 'button'; useVin.dataset.vehicleAction = 'use-vin';
        note.append(' ', useVin);
        card.append(note);
      }

      if (vehicle.source !== 'ymm') {
        const details = element('details', 'vehicle-card__more');
        details.append(element('summary', '', t.vehicleDetails));
        const list = element('dl', 'vehicle-card__details');
        const engine = [vehicle.cylinders ? `${vehicle.cylinders} ${t.cylinders}` : '', vehicle.displacement > 0 ? `${vehicle.displacement.toFixed(1)}L` : ''].filter(Boolean).join(' · ');
        [[t.trim, vehicle.trim], [t.body, shortBody(vehicle.body)], [t.drive, shortDrive(vehicle.drive)], [t.engine, engine],
          [t.fuel, vehicle.fuel], [t.transmission, shortTransmission(vehicle.transmission)], [t.tpms, vehicle.tpms]].forEach(([label, value]) => {
          if (!value) return;
          const row = element('div');
          row.append(element('dt', '', label), element('dd', '', value));
          list.append(row);
        });
        if (list.children.length) details.append(list);
        if (photo) details.append(element('p', 'vehicle-card__note', t.photoNote));
        details.append(element('p', 'vehicle-card__note', vehicle.source === 'plate' ? t.plateSource : t.source));
        card.append(details);
      }

      const notes = element('details', 'vehicle-card__more');
      notes.append(element('summary', '', t.fitmentNotes));
      if (fitment.bolt) notes.append(element('p', 'vehicle-card__note', t.boltNote));
      notes.append(element('p', 'vehicle-card__note', t.oemNote));
      if (fitment.boltSource) {
        const link = element('a', 'vehicle-card__link', fitment.boltProvider ? `${fitment.boltProvider} · CC BY 4.0` : t.boltSource);
        link.href = fitment.boltSource; link.target = '_blank'; link.rel = 'noopener';
        notes.append(link);
      }
      if (fitment.oem?.source) {
        const link = element('a', 'vehicle-card__link', t.manualSource);
        link.href = fitment.oem.source; link.target = '_blank'; link.rel = 'noopener';
        notes.append(link);
      }
      card.append(notes, element('p', 'vehicle-card__saved', t.saved));
      return card;
    }

    async fillYears() {
      const select = this.ymm.year;
      if (!select || this.yearsReady) return;
      select.disabled = true;
      select.options[0].textContent = this.t.ymmLoading;
      const list = await this.loadBoltReference();
      select.options[0].textContent = this.t.year;
      if (!list?.length) { this.showMessage(this.t.ymmError); return; }
      const years = new Set();
      list.forEach((item) => { for (let year = item.years[0]; year <= item.years[1]; year += 1) years.add(year); });
      [...years].sort((a, b) => b - a).forEach((year) => select.add(new Option(String(year), String(year))));
      select.disabled = false;
      this.yearsReady = true;
      if (document.activeElement?.dataset?.lookupMode === 'ymm') select.focus();
    }

    resetSelect(select, placeholder) {
      select.replaceChildren(new Option(placeholder, ''));
      select.disabled = true;
    }

    async fillMakes() {
      const year = Number(this.ymm.year.value);
      this.resetSelect(this.ymm.make, this.t.make);
      this.resetSelect(this.ymm.model, this.t.model);
      this.showMessage('');
      if (!year) return;
      const list = await this.loadBoltReference() || [];
      const makes = [...new Set(list.filter((item) => year >= item.years[0] && year <= item.years[1]).map((item) => item.make))].sort();
      makes.forEach((make) => this.ymm.make.add(new Option(make, make)));
      this.ymm.make.disabled = !makes.length;
    }

    async fillModels() {
      const year = Number(this.ymm.year.value);
      const make = this.ymm.make.value;
      this.resetSelect(this.ymm.model, this.t.model);
      this.showMessage('');
      if (!make) return;
      const list = await this.loadBoltReference() || [];
      list.map((item, index) => ({ item, index }))
        .filter(({ item }) => item.make === make && year >= item.years[0] && year <= item.years[1])
        .sort((a, b) => (a.item.label || a.item.model).localeCompare(b.item.label || b.item.model))
        .forEach(({ item, index }) => this.ymm.model.add(new Option(item.label || item.model, String(index))));
      this.ymm.model.disabled = false;
    }
  }

  customElements.define('vehicle-lookup', VehicleLookup);
})();

